// @ts-strict-ignore
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Matrix4, Vector3 } from 'three';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { Dictionary } from 'typescript-collections';
import { ArticleLineHolder } from '@article/helper/article-line-holder';
import { ArticleUtils } from '@article/util/article-utils';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { ArticleRotationGroup } from '@article/rotation/article-rotation-group';
import { ArticleRotationResult } from '@article/rotation/article-rotation-result';
import { LabelingArticleRotationHelper } from '../../label/helper/labeling-article-rotation.helper';
import { AlertHelper } from '@shared/alert/alert.helper';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { Injectable } from '@angular/core';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

@Injectable({ providedIn: 'root' })
export class RotateArticlePresenter {
  public articleRotationGroups: ArticleRotationGroup[];

  constructor(
    private _alertHelper: AlertHelper,
    private _sceneManager: SceneManager,
    private _articleContainerUtils: ArticleContainerUtils,
    private connectionInfoService: ConnectionInfoService,
    private _labelingArticleRotationHelper: LabelingArticleRotationHelper,
    private _insertArticleHelperFactory: InsertArticleHelperFactory,
    private _insertArticleUtils: InsertArticleUtils,
    private _containerService: ContainerService,
    private _articleLineHolder: ArticleLineHolder,
    private _articleConnectionUtils: ArticleConnectionService
  ) {}

  public rotateArticleContainersAsGroup(
    groupedContainers: ArticleContainer[],
    angle: number,
    inverseRotation: boolean = false,
    considerConnectionAngles: boolean = true,
    updateRotationAngleProperty: boolean = true,
    childContainers: ArticleContainer[] = null,
    isSwitchRotation: boolean = false
  ): boolean {
    if (ArrayUtils.isEmpty(groupedContainers)) {
      return false;
    }
    const isArticleGroupConnectedToRangeOnLineArticle: boolean = this._articleConnectionUtils.isArticleConnectedToRangeOnLineArticle(
      groupedContainers[0]
    );
    const connectionRectangles: Rectangle3D[] = [];
    for (const container of groupedContainers) {
      const connections: Connection[] = this.findGivingConnectionsWithAngle(
        container,
        angle,
        considerConnectionAngles,
        inverseRotation,
        true,
        isSwitchRotation
      );
      for (const connection of connections) {
        let calculatedRectangle: Rectangle3D = null;
        if (isArticleGroupConnectedToRangeOnLineArticle) {
          calculatedRectangle = this.connectionInfoService.calculateRectangleByConnection(connection, true);
        } else {
          calculatedRectangle = Rectangle3D.fromArray(MathUtils.localToGlobalArray(container, connection.localRectangle.toArray()));
        }
        connectionRectangles.push(calculatedRectangle);
      }
    }
    if (connectionRectangles.length > 0) {
      const firstRectangle: Rectangle3D = connectionRectangles[0];
      const rotationNormal: Vector3 = MathUtils.getNormalFromFirstPoint(firstRectangle.point1, firstRectangle.point2, firstRectangle.point3);
      const pivotPoint: Vector3 = this.calculateMiddlePointByRectangles(connectionRectangles);
      const recreateConnectionUsages: boolean = this.isAvailableRecreateConnectionUsages(groupedContainers[0]);
      return this.rotateArticles(
        groupedContainers,
        inverseRotation ? -angle : angle,
        rotationNormal,
        pivotPoint,
        recreateConnectionUsages,
        updateRotationAngleProperty,
        false,
        childContainers,
        isSwitchRotation
      );
    }
    return false;
  }

  private rotateArticles(
    containers: ArticleContainer[],
    angle: number,
    normale: Vector3,
    pivotPoint: Vector3,
    recreateConnectionUsages: boolean = true,
    updateRotationAngleProperty: boolean = true,
    updateChildRotationAngleProperty: boolean = false,
    childContainers: ArticleContainer[] = null,
    isSwitchRotation: boolean = false,
    rotateChilds: boolean = true
  ): boolean {
    if (!containers || containers.length === 0) {
      return false;
    }
    if (!this._insertArticleUtils.isRotationArticlesOnRailAvailable(containers, this._insertArticleHelperFactory)) {
      return false;
    }
    const usageIdToDefaultTransformation: Dictionary<string, Matrix4> = this.buildArticlesTransformationMap(containers);
    // angle is inverted here since rotation in html5 version is inverse to previously used 3D engine
    const applyAngle: number = angle * -1;
    this.applyRotationToArticleContainers(containers, applyAngle, normale, pivotPoint);
    let articlesRotated: boolean = true;
    if (recreateConnectionUsages) {
      const currentMetaUsageIds: MetaElementUsageIdentityVO[] = this.collectArticleGivingConnectionIds(containers);
      articlesRotated = this.recreateMetaUsages(containers, currentMetaUsageIds);
      if (!articlesRotated) {
        this.applyTransformationToArticleContainers(containers, usageIdToDefaultTransformation);
        this.recreateMetaUsages(containers, currentMetaUsageIds);
      }
    }
    if (articlesRotated) {
      const rotatedArticleGuids: string[] = [];
      let collectChildContainers: boolean = false;
      if (!childContainers) {
        collectChildContainers = true;
        childContainers = [];
      }
      for (const rotatedContainer of containers) {
        rotatedArticleGuids.push(rotatedContainer.articleGUID);
        if (updateRotationAngleProperty) {
          ArticleUsageUtils.updateAppliedRotationAngle(rotatedContainer.articleUsage, angle);
        }
        if (isSwitchRotation && updateRotationAngleProperty) {
          ArticleUsageUtils.toggleIsSwitchedState(rotatedContainer.articleUsage);
        }
        if (collectChildContainers) {
          ArrayUtils.mergeArrays(childContainers, this._containerService.getConnectedArticleContainers(rotatedContainer.articleUsage));
        }
      }

      if(rotateChilds) {
        ArrayUtils.mergeArrays(
          rotatedArticleGuids,
          this.rotateChildArticles(childContainers, angle, normale, pivotPoint, updateChildRotationAngleProperty, rotatedArticleGuids, isSwitchRotation)
        );
      }
      this._articleLineHolder.clearArticleLineCacheByGUIDs(rotatedArticleGuids);
    }
    return articlesRotated;
  }

  private rotateChildArticles(
    childContainers: ArticleContainer[],
    angle: number,
    normale: Vector3,
    pivotPoint: Vector3,
    updateRotationAngleProperty: boolean = false,
    rotatedArticleGuids: string[] = null,
    isSwitchRotation: boolean = false
  ): string[] {
    if (!childContainers || childContainers.length === 0) {
      return [];
    }
    let alreadyRotatedArticleGuids: string[] = !rotatedArticleGuids ? [] : rotatedArticleGuids.concat();
    const connectedArticles: ArticleContainer[] = [];
    for (const childContainer of childContainers) {
      if (alreadyRotatedArticleGuids.indexOf(childContainer.articleGUID) !== -1) {
        continue;
      }
      // angle is inverted here since rotation in html5 version is inverse to previously used 3D engine
      const applyAngle: number = angle * -1;
      this.applyRotationToArticleContainer(childContainer, applyAngle, normale, pivotPoint);
      if (updateRotationAngleProperty) {
        ArticleUsageUtils.updateAppliedRotationAngle(childContainer.articleUsage, angle);
      }
      if (isSwitchRotation && updateRotationAngleProperty) {
        ArticleUsageUtils.toggleIsSwitchedState(childContainer.articleUsage);
      }
      alreadyRotatedArticleGuids.push(childContainer.articleGUID);
      if (!isSwitchRotation && ArticleUtils.isLabelingArticle(childContainer.article)) {
        this._labelingArticleRotationHelper.rotateLabelingArticle(this, childContainer);
      }
      ArrayUtils.mergeArrays(connectedArticles, this._containerService.getConnectedArticleContainers(childContainer.articleUsage));
    }
    // TODO: (from flex) this needs to be checked, here parameters were to completely transferred to recursive method call, so always false and null
    // used for updateRotationAngleProperty and rotatedArticleGuids
    alreadyRotatedArticleGuids = alreadyRotatedArticleGuids.concat(
      this.rotateChildArticles(connectedArticles, angle, normale, pivotPoint, false, null, isSwitchRotation)
    );
    return alreadyRotatedArticleGuids;
  }

  private detachAllMetaElementUsages(articleContainers: ArticleContainer[]): void {
    if (!articleContainers) {
      return;
    }
    for (const container of articleContainers) {
      container.detachAllMetaElementUsages();
    }
  }

  private recreateMetaUsages(articleContainers: ArticleContainer[], metaUsageIds: MetaElementUsageIdentityVO[]): boolean {
    if (
      !articleContainers ||
      articleContainers.length === 0 ||
      !metaUsageIds ||
      metaUsageIds.length === 0 ||
      (metaUsageIds.length === 1 && articleContainers.length === 1)
    ) {
      return true;
    }
    this.detachAllMetaElementUsages(articleContainers);

    for (const freeMetaUsageId of metaUsageIds) {
      const articleContainer: ArticleContainer = this._articleContainerUtils.findArticleContainerByGUID(
        freeMetaUsageId.articleUsageId,
        articleContainers
      );
      if (!articleContainer) {
        continue;
      }
      const freeCube: Connection = articleContainer.findFirstConnectionByName(freeMetaUsageId.metaElementName);
      const requiredConnection: Connection = this.connectionInfoService.findRequiredConnectionInGlobal(freeCube, [articleContainer.articleGUID]);
      if (!requiredConnection) {
        return false;
      }
      articleContainer.attachMetaElementUsageByName(freeMetaUsageId.metaElementName, requiredConnection);
    }
    return true;
  }

  private collectArticleGivingConnectionIds(articleContainers: ArticleContainer[]): MetaElementUsageIdentityVO[] {
    if (!articleContainers) {
      return [];
    }
    let givingAreaIds: MetaElementUsageIdentityVO[] = [];
    for (const container of articleContainers) {
      givingAreaIds = givingAreaIds.concat(container.getCurrentArticleGivingAreaIds());
    }
    return givingAreaIds;
  }

  private applyRotationToArticleContainers(containers: ArticleContainer[], angle: number, normale: Vector3, pivotPoint: Vector3): void {
    if (!containers) {
      return;
    }
    for (const container of containers) {
      this.applyRotationToArticleContainer(container, angle, normale, pivotPoint);
    }
  }

  private applyRotationToArticleContainer(container: ArticleContainer, angle: number, normale: Vector3, pivotPoint: Vector3): void {
    const scale: Vector3 = new Vector3(container.scale.x, container.scale.y, container.scale.z);
    const articleTransform: Matrix4 = new Matrix4();
    const roundNormale: Vector3 = new Vector3(
      MathUtils.roundNumberValue(normale.x),
      MathUtils.roundNumberValue(normale.y),
      MathUtils.roundNumberValue(normale.z)
    );
    articleTransform.makeScale(1 / scale.x, 1 / scale.y, 1 / scale.z);
    container.applyMatrix4(articleTransform);
    MeshUtils.rotateByAxis(container, MathUtils.toRadians(angle), roundNormale, pivotPoint);
    articleTransform.makeScale(scale.x, scale.y, scale.z);

    this.applyTransformationToContainer(container, articleTransform);
  }

  private applyTransformationToArticleContainers(containers: ArticleContainer[], usageIdToArticleTransformation: Dictionary<string, Matrix4>): void {
    if (!containers || !usageIdToArticleTransformation) {
      return;
    }
    for (const container of containers) {
      if (!usageIdToArticleTransformation.containsKey(container.articleGUID)) {
        continue;
      }
      const articleTransform: Matrix4 = usageIdToArticleTransformation.getValue(container.articleGUID);
      container.matrix.identity();
      this.applyTransformationToContainer(container, articleTransform);
    }
  }

  private applyTransformationToContainer(container: ArticleContainer, transformMatrix: Matrix4): void {
    container.applyMatrix4(transformMatrix.clone());
    container.updateMatrixWorld(true);
    container.refreshContainer();
  }

  private buildArticlesTransformationMap(containers: ArticleContainer[]): Dictionary<string, Matrix4> {
    if (!containers) {
      return null;
    }
    const usageIdToArticleTransformation: Dictionary<string, Matrix4> = new Dictionary<string, Matrix4>();
    for (const container of containers) {
      container.updateMatrix();
      usageIdToArticleTransformation.setValue(container.articleGUID, container.matrix.clone());
    }
    return usageIdToArticleTransformation;
  }

  private isAvailableRecreateConnectionUsages(container: ArticleContainer): boolean {
    if (!container) {
      return false;
    }
    const parentConnectionUsageIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(container.articleUsage);
    if (parentConnectionUsageIds.length === 0) {
      return false;
    }
    const parentMetaElementUsageIdentity: MetaElementUsageIdentityVO = parentConnectionUsageIds[0];
    const parentContainer: ArticleContainer = this._containerService.findArticleContainerInGlobal(parentMetaElementUsageIdentity.articleUsageId);
    const parentMetaElement: MetaElementVO = parentContainer.findMetaElementByName(parentMetaElementUsageIdentity.metaElementName);
    return !ConnectionInfoUtils.isRangeOnLineConnection(parentMetaElement);
  }

  private calculateMiddlePointByRectangles(rectangles: Rectangle3D[]): Vector3 {
    const commonRectangle: Rectangle3D = ConnectionInfoUtils.calculateCommonRectangle(rectangles, true, true);
    return MathUtils.calculateMiddlePointByRectangle(commonRectangle);
  }

  private findGivingConnectionsWithAngle(
    container: ArticleContainer,
    angle: number,
    considerConnectionAngles: boolean,
    inverseRotation: boolean,
    considerOnlyInvolvedGivingConnections: boolean = true,
    isSwitchRotation: boolean = false
  ): Connection[] {
    const givingConnections: Connection[] = [];
    let givingConnectionAreas: ConnectionAreaVO[] = [];
    if (considerOnlyInvolvedGivingConnections) {
      const givingAreaIds: MetaElementUsageIdentityVO[] = container.getCurrentArticleGivingAreaIds();
      for (const identity of givingAreaIds) {
        const meta: MetaElementVO = container.findMetaElementByName(identity.metaElementName);
        if (meta && meta instanceof ConnectionAreaVO) {
          givingConnectionAreas.push(meta);
        }
      }
    } else {
      givingConnectionAreas = ArticleUtils.getGivingConnectionsInActiveSet(container.article, container.articleUsage);
    }

    for (const givingArea of givingConnectionAreas) {
      let rotationAngles: number[];
      if (isSwitchRotation) {
        let switchRotationAngle: number = PropertyUtils.getSwitchRotationAngle([givingArea]);
        const isSwitched: boolean = ArticleUsageUtils.isSwitched(container.articleUsage);
        if (isSwitched) {
          switchRotationAngle *= -1;
        }
        rotationAngles = [switchRotationAngle];
        if (inverseRotation) {
          angle *= -1;
        }
      } else {
        rotationAngles = PropertyUtils.getRotationAngles([givingArea]);
      }
      if (rotationAngles.indexOf(angle) === -1 && considerConnectionAngles) {
        continue;
      }

      const connection: Connection = container.findConnectionByName(givingArea.name);
      if (connection != null) {
        givingConnections.push(connection);
      }
    }

    return givingConnections;
  }

  public rotateArticleContainer(
    container: ArticleContainer,
    angle: number,
    inverseRotation: boolean = false,
    considerConnectionAngles: boolean = true,
    considerOnlyInvolvedGivingConnections: boolean = true,
    recreateConnectionUsages: boolean = true,
    updateRotationAngleProperty: boolean = true,
    childContainers: ArticleContainer[] = null,
    isSwitchRotation: boolean = false,
    rotateChilds: boolean = true
  ): boolean {
    let articlesRotated: boolean = false;
    if (!this._articleContainerUtils.isArticleCombined(container) || isSwitchRotation) {
      const connections: Connection[] = this.findGivingConnectionsWithAngle(
        container,
        angle,
        considerConnectionAngles,
        inverseRotation,
        considerOnlyInvolvedGivingConnections,
        isSwitchRotation
      );
      if (connections.length > 0) {
        const firstConnection: Connection = connections[0];
        const rectPoints: Vector3[] = firstConnection.compositionRectangle.toArray();
        const normale: Vector3 = MathUtils.getNormalFromFirstPoint(rectPoints[0], rectPoints[1], rectPoints[2]);
        const pivotPoint: Vector3 = MathUtils.localToGlobal(container, this.calculateMiddlePointByConnections(connections));
        articlesRotated = this.rotateArticles(
          [container],
          inverseRotation ? -angle : angle,
          normale,
          pivotPoint,
          recreateConnectionUsages,
          updateRotationAngleProperty,
          false,
          childContainers,
          isSwitchRotation,
          rotateChilds
        );
      }
    }
    return articlesRotated;
  }

  private calculateMiddlePointByConnections(connections: Connection[]): Vector3 {
    const rectangles: Rectangle3D[] = [];
    for (const conn of connections) {
      rectangles.push(conn.localRectangle.clone());
    }
    return this.calculateMiddlePointByRectangles(rectangles);
  }

  public rotateArticleRotationGroup(
    rotationGroup: ArticleRotationGroup,
    angle: number,
    inverseRotation: boolean = false,
    considerConnectionAngles: boolean = true,
    isSwitchRotation: boolean = false
  ): ArticleRotationResult {
    if (!rotationGroup) {
      return null;
    }
    const rotationResult: ArticleRotationResult = new ArticleRotationResult();
    rotationResult.groupedRotation = rotationGroup.rotateAsGroup;
    rotationResult.allArticlesRotated = true;
    const containersToRotate: ArticleContainer[] = rotationGroup.articleContainers;
    if (rotationGroup.rotateAsGroup) {
      const groupedArticlesRotated: boolean = this.rotateArticleContainersAsGroup(
        containersToRotate,
        angle,
        inverseRotation,
        considerConnectionAngles,
        true,
        null,
        isSwitchRotation
      );
      if (groupedArticlesRotated) {
        rotationResult.rotatedArticleUsageIds = ArticleContainerUtils.extractArticleGUIDs(containersToRotate);
      }
      rotationResult.allArticlesRotated = groupedArticlesRotated;
    } else {
      for (const container of containersToRotate) {
        const recreateConnectionUsages: boolean = this.isAvailableRecreateConnectionUsages(container);
        const articleRotated: boolean = this.rotateArticleContainer(
          container,
          angle,
          inverseRotation,
          considerConnectionAngles,
          true,
          recreateConnectionUsages,
          true,
          null,
          isSwitchRotation
        );
        if (articleRotated) {
          rotationResult.rotatedArticleUsageIds.push(container.articleGUID);
        }
        rotationResult.allArticlesRotated = rotationResult.allArticlesRotated && articleRotated;
      }
    }
    return rotationResult;
  }

  public rotate(angle: number, inverseRotation: boolean = false, isSwitchRotation: boolean = false): ArticleRotationResult[] {
    const rotationResults: ArticleRotationResult[] = [];
    let isRotated: boolean = true;
    for (const rotationGroup of this.articleRotationGroups) {
      const rotationResult: ArticleRotationResult = this.rotateArticleRotationGroup(
        rotationGroup,
        angle,
        inverseRotation,
        true,
        isSwitchRotation
      );
      if (rotationResult) {
        rotationResults.push(rotationResult);
        isRotated = isRotated && rotationResult.allArticlesRotated;
      }
    }
    if (!isRotated) {
      this.showNoRotationAngleAvailableMessage();
    }
    this._sceneManager.setFocusAtCanvas();
    this._sceneManager.renderScene();
    return rotationResults;
  }

  public showNoRotationAngleAvailableMessage(): void {
    this._alertHelper.showError("rotateArticlePopup.title.error_rotating_article", "rotateArticlePopup.message.error_rotating_article");
    this._sceneManager.setFocusAtCanvas();
  }
}
